import { supabase } from '../supabase'
import { DEFAULT_KITCHEN_BELL, isKitchenBell, type KitchenBellKey } from '../../utils/kitchenBell'
import { readCache, writeCache } from '../../utils/localCache'
import {
  DEFAULT_DISCOUNTS,
  DEFAULT_STUDENT_DISCOUNT,
  type Features,
  type NetworkPrinter,
  type ReceiptSettings,
  type TenantType,
  DEFAULT_LOYALTY,
  type LoyaltySettings,
} from '../../types'

/**
 * The institution's own settings: what it is called, what discounts it
 * offers, what its receipts look like.
 *
 * Live rather than read once. The owner changes a discount on one screen and
 * the till has to pick it up without anyone signing out, during service that
 * is the difference between a correction and a queue.
 */
export interface TenantSettings {
  name: string
  studentDiscountPercent: number
  discounts: number[]
  receipt: ReceiptSettings
  cashReceipt: ReceiptSettings
  type: TenantType
  /**
   * Which optional parts this institution has been given. Live like the rest
   * of this object: the system administrator switches one on and the till
   * picks it up without anyone signing out.
   */
  features: Features
  /**
   * When a free trial ends, ms since epoch; null = a permanent institution
   * (opened with a code, or before trials existed). See deneme-suresi.sql.
   */
  trialEndsAt: number | null
  /** Contact number, `+90…`; null when never given. Owner-only on screen. */
  contactPhone: string | null
  /** Paket ('start' | 'plus' | 'max', plans.ts); paketsiz kurumda null. */
  plan: string | null
  /** Paketin ödeme dönemi ('aylik' | 'yillik'); bilinmiyorsa null. */
  planPeriod: string | null
  /** Ağ yazıcıları (supabase/yazicilar.sql); kolon yoksa boş. */
  printers: NetworkPrinter[]
  /** Fiş yazıcısı kullanıyor mu (tenants.use_printer). Yoksa açık. */
  usePrinter: boolean
  /** Müşteri sadakat kuralı (tenants.loyalty); yazılmamışsa varsayılan. */
  loyalty: LoyaltySettings
  /** İşletmenin logosu (tenants.logo_url, supabase/logo.sql); yoksa null. */
  logoUrl: string | null
  /** Mutfak ekranının yeni fiş zili (tenants.kitchen_bell, utils/kitchenBell). */
  kitchenBell: KitchenBellKey
}

export function defaultSettings(tenantId: string): TenantSettings {
  return {
    name: tenantId,
    studentDiscountPercent: DEFAULT_STUDENT_DISCOUNT,
    discounts: DEFAULT_DISCOUNTS,
    receipt: {},
    cashReceipt: {},
    type: 'restaurant',
    // Nothing is on until it is written. An institution that predates the
    // idea of features sees exactly what it saw before.
    features: {},
    trialEndsAt: null,
    contactPhone: null,
    plan: null,
    planPeriod: null,
    printers: [],
    usePrinter: true,
    loyalty: DEFAULT_LOYALTY,
    logoUrl: null,
    kitchenBell: DEFAULT_KITCHEN_BELL,
  }
}

/** Shapes the two databases' rows into the same object. */
function shape(
  tenantId: string,
  row: Record<string, unknown> | null | undefined,
  keys: { display: string; discount: string; cash: string },
): TenantSettings {
  const base = defaultSettings(tenantId)
  if (!row) return base

  return {
    name: (row[keys.display] as string) || tenantId,
    studentDiscountPercent:
      typeof row[keys.discount] === 'number'
        ? (row[keys.discount] as number)
        : base.studentDiscountPercent,
    discounts: Array.isArray(row.discounts) ? (row.discounts as number[]) : base.discounts,
    receipt: (row.receipt ?? {}) as ReceiptSettings,
    cashReceipt: (row[keys.cash] ?? {}) as ReceiptSettings,
    type: 'restaurant',
    features: (row.features ?? {}) as Features,
    trialEndsAt: typeof row.trial_ends_at === 'string' ? Date.parse(row.trial_ends_at) : null,
    contactPhone: typeof row.contact_phone === 'string' ? row.contact_phone : null,
    logoUrl: typeof row.logo_url === 'string' && row.logo_url ? row.logo_url : null,
    plan: typeof row.plan === 'string' && row.plan ? row.plan : null,
    planPeriod: typeof row.plan_period === 'string' && row.plan_period ? row.plan_period : null,
    printers: Array.isArray(row.printers) ? (row.printers as NetworkPrinter[]) : [],
    // Yalnız açıkça false ise kapalı: sütun henüz yokken de basılsın.
    usePrinter: row.use_printer !== false,
    loyalty: { ...DEFAULT_LOYALTY, ...((row.loyalty ?? {}) as Partial<LoyaltySettings>) },
    kitchenBell: isKitchenBell(row.kitchen_bell) ? row.kitchen_bell : DEFAULT_KITCHEN_BELL,
  }
}

export function subscribeTenantSettings(
  tenantId: string,
  onData: (settings: TenantSettings) => void,
): () => void {
  if (!supabase) {
    onData(defaultSettings(tenantId))
    return () => {}
  }

  let cancelled = false

  // Önce cihazdaki kopya (28 Eki 2026): açılışta ve internetsizken özellikler,
  // ad, fiş ayarları beklemeden gelsin.
  const cached = readCache<TenantSettings>(tenantId, 'settings')
  if (cached) onData({ ...defaultSettings(tenantId), ...cached })

  const read = async () => {
    const { data, error } = await supabase!
      .from('tenants')
      .select('*')
      .eq('id', tenantId)
      .maybeSingle()

    if (cancelled) return
    if (error) {
      // Falling back to defaults rather than leaving the header blank: the
      // till is still perfectly usable with standard discounts and a plain
      // receipt, and an empty screen looks broken.
      console.error('[auth] kurum ayarları okunamadı', error)
      // Kopya varsa o kalıyor: varsayılana dönmek internetsizken bütün
      // özellikleri kapatırdı.
      if (!cached) onData(defaultSettings(tenantId))
      return
    }
    const fresh = shape(tenantId, data, {
      display: 'display_name',
      discount: 'student_discount_percent',
      cash: 'cash_receipt',
    })
    writeCache(tenantId, 'settings', fresh)
    onData(fresh)
  }

  void read()

  // Postgres changes push straight to the client, the way the Firestore
  // listener did.
  //
  // Unique per subscription: Supabase keeps one channel per name and
  // refuses to attach a listener to one that has already subscribed, so a
  // remount would throw.
  const channel = supabase
    .channel(`tenant:${tenantId}:${crypto.randomUUID()}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'tenants', filter: `id=eq.${tenantId}` },
      () => void read(),
    )
    .subscribe()

  return () => {
    cancelled = true
    void supabase!.removeChannel(channel)
  }
}
